import environment from '@square/environment-utils/environment';
// We only want to inject the scripts once for each component lib that uses
// the shim. There could be multiple components from one component lib - they
// should use the same scripts. This map keeps track of how many components
// are being rendered from the same lib. We remove the scripts when the last
// component from ANY lib is unmounted.
export const lifecycleReadyMap = new Map();
const DEV_SINGLE_SPA_REACT_MODULE_PATH = 'src/hmr-mode/dashboard-modules.tsx';
const DEV_GLOBAL_COMPONENT_VAR_NAME_PREFIX = 'dashboardReactLifecycles_';
const DEV_GLOBAL_COMPONENT_EVENT_PREFIX = 'dashboardReactLifecyclesReady_';
export const DEV_GLOBAL_COMPONENT_EVENT_SCRIPT_ID_PREFIX = 'dashboardReactLifecyclesReady-script_';
const DEV_REACT_REFRESH_SCRIPT_ID_PREFIX = 'reactRefresh-script_';
export const DEFAULT_CONFIG = {
  devBasePort: 4201,
  devSingleSpaReactModulePath: DEV_SINGLE_SPA_REACT_MODULE_PATH
};
export function addScript({
  id,
  src,
  isText = false,
  devBaseUrl
}) {
  if (document.getElementById(id || src)) {
    return;
  }
  const script = document.createElement('script');
  script.type = 'module';
  script.id = id || src;
  if (isText) {
    script.textContent = src;
  } else {
    script.src = `${devBaseUrl}/${src}`;
  }
  document.getElementsByTagName('head')[0].appendChild(script);
}
function removeScript(id) {
  const script = document.getElementById(id);
  if (script) {
    script.remove();
  }
}
export function removeReactDevScripts(config) {
  const globalComponentEvent = `${DEV_GLOBAL_COMPONENT_EVENT_PREFIX}${config.devBasePort}`;
  const remainingCount = lifecycleReadyMap.get(globalComponentEvent) || 0;
  if (remainingCount > 1) {
    lifecycleReadyMap.set(globalComponentEvent, remainingCount - 1);
  } else {
    lifecycleReadyMap.delete(globalComponentEvent);
    removeScript(`${DEV_GLOBAL_COMPONENT_EVENT_SCRIPT_ID_PREFIX}${config.devBasePort}`);
  }
}
export async function shouldEnableHotReload({
  devBaseUrl,
  devSingleSpaReactModulePath
}) {
  if ((environment().isDevelopment || environment().isStaging) && !environment().isTesting) {
    try {
      const {
        ok
      } = await fetch(`${devBaseUrl}/${devSingleSpaReactModulePath}`);
      return ok;
    } catch {
      return false;
    }
  }
  return false;
}
export function addLifecycleReadyListener(globalComponentEvent, globalComponentVar, resolve) {
  const initialCount = lifecycleReadyMap.get(globalComponentEvent);
  if (initialCount) {
    lifecycleReadyMap.set(globalComponentEvent, initialCount + 1);
    resolve(window[globalComponentVar]);
    return;
  }
  // This event will be dispatched when the module federation is ready
  // The emit is coming from the last script being added below
  window.addEventListener(globalComponentEvent, () => {
    lifecycleReadyMap.set(globalComponentEvent, 1);
    resolve(window[globalComponentVar]);
  }, {
    once: true
  });
}
function _injectComponentHotReloadScripts(config) {
  return new Promise((resolve, reject) => {
    (async () => {
      const {
        devSingleSpaReactModulePath,
        devBasePort
      } = config;
      const devBaseUrl = `http://localhost:${devBasePort}`;
      const shouldEnable = await shouldEnableHotReload({
        devBaseUrl,
        devSingleSpaReactModulePath
      });
      if (!shouldEnable) {
        reject(new Error('Hot reload is not enabled'));
        return;
      }

      // Add the port to the end of the variable name to make event names unique for
      // multiple shims running HMR mode
      const globalComponentVar = `${DEV_GLOBAL_COMPONENT_VAR_NAME_PREFIX}${devBasePort}`;
      const globalComponentEvent = `${DEV_GLOBAL_COMPONENT_EVENT_PREFIX}${devBasePort}`;
      addLifecycleReadyListener(globalComponentEvent, globalComponentVar, resolve);

      // These first two imports are for Vite HMR support
      // more here https://vitejs.dev/guide/backend-integration
      addScript({
        src: '@vite/client',
        devBaseUrl
      });
      addScript({
        src: `
        import RefreshRuntime from '${devBaseUrl}/@react-refresh';
        RefreshRuntime.injectIntoGlobalHook(window);
        window.$RefreshReg$ = () => {};
        // eslint-disable-next-line unicorn/consistent-function-scoping
        window.$RefreshSig$ = () => (type) => type;
        window.__vite_plugin_react_preamble_installed__ = true;`,
        id: `${DEV_REACT_REFRESH_SCRIPT_ID_PREFIX}${devBasePort}`,
        isText: true
      });

      // This is the entry point for the module federation
      // Any change from this file will trigger HMR
      addScript({
        src: devSingleSpaReactModulePath,
        devBaseUrl
      });

      // Access the modules exported from the module above, assign it
      // to the global window object and dispatching a custom event to Ember
      addScript({
        src: `
        import { mount, unmount, bootstrap, update } from '${devBaseUrl}/${devSingleSpaReactModulePath}';
        window.${globalComponentVar} = { mount, unmount, bootstrap, update };
        dispatchEvent(new CustomEvent('${globalComponentEvent}'));
        `,
        id: `${DEV_GLOBAL_COMPONENT_EVENT_SCRIPT_ID_PREFIX}${devBasePort}`,
        isText: true
      });
    })();
  });
}
export const reactLibraryUtilMocks = {
  injectComponentHotReloadScripts(...args) {
    return _injectComponentHotReloadScripts(...args);
  }
};
export function injectComponentHotReloadScripts(...args) {
  return reactLibraryUtilMocks.injectComponentHotReloadScripts(...args);
}